import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Id } from '../../../../convex/_generated/dataModel'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/auth-context'
import {
  Bot,
  BookOpen,
  SlidersHorizontal,
  FlaskConical,
  ChevronDown,
  ChevronRight,
  Search,
  X,
  ToggleLeft,
  ToggleRight,
  Wrench,
  Sparkles,
  Globe,
  Monitor,
  Zap,
  Play,
  FileText,
  Copy,
  Check,
  ChevronsUpDown,
  RotateCcw,
  ArrowRight,
  BarChart3,
  ExternalLink,
} from 'lucide-react'

type TabId = 'skills' | 'conditionals' | 'playground'

const SURFACE_COLORS: Record<string, string> = {
  all: 'bg-neutral-500/15 text-neutral-400 border-neutral-500/20',
  cli: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
  web: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
}

function Badge({ label, colorClass }: { label: string; colorClass: string }) {
  return (
    <span className={cn('text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wider', colorClass)}>
      {label}
    </span>
  )
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  return `${Math.floor(diff / 86_400_000)}d ago`
}

function Sparkline({ data, color = '#22d3ee', height = 24, width = 80 }: {
  data: number[]
  color?: string
  height?: number
  width?: number
}) {
  if (data.length < 2) return null
  const max = Math.max(...data, 1)
  const step = width / (data.length - 1)
  const points = data.map((v, i) => `${i * step},${height - (v / max) * (height - 2)}`).join(' ')
  const areaPoints = `0,${height} ${points} ${width},${height}`
  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <polyline fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" points={points} />
      <polygon fill={color} fillOpacity={0.08} points={areaPoints} />
    </svg>
  )
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
      className={cn(
        'p-1 rounded transition-colors',
        copied ? 'text-emerald-400' : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800',
      )}
      title={copied ? 'Copied' : 'Copy to clipboard'}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

function CollapsibleSection({ title, defaultOpen = false, count, children, headerExtra, copyText, onOpenChange }: {
  title: string
  defaultOpen?: boolean
  count?: number
  children: React.ReactNode
  headerExtra?: React.ReactNode
  copyText?: string
  onOpenChange?: (open: boolean) => void
}) {
  const [open, setOpen] = useState(defaultOpen)
  const toggle = () => {
    const next = !open
    setOpen(next)
    onOpenChange?.(next)
  }
  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-neutral-900/50 transition-colors"
      >
        {open ? <ChevronDown className="w-4 h-4 text-neutral-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-neutral-500 flex-shrink-0" />}
        <span className="text-sm font-medium text-neutral-200">{title}</span>
        {count != null && (
          <span className="text-[10px] bg-neutral-800 text-neutral-400 px-1.5 py-0.5 rounded-full">{count}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {copyText && <CopyBtn text={copyText} />}
          {headerExtra}
        </div>
      </button>
      {open && <div className="px-4 pb-4 border-t border-neutral-800/50">{children}</div>}
    </div>
  )
}

function PromptPreview({ text, label }: { text: string; label?: string }) {
  const charCount = text.length
  const tokenEstimate = Math.round(charCount / 4)
  return (
    <div className="space-y-2 mt-3">
      <div className="flex items-center gap-3 text-[11px] text-neutral-500">
        {label && <span className="font-medium text-neutral-400">{label}</span>}
        <span>{charCount.toLocaleString()} chars</span>
        <span>~{tokenEstimate.toLocaleString()} tokens</span>
        <CopyBtn text={text} />
      </div>
      <pre className="bg-neutral-950 border border-neutral-800 rounded-lg p-4 text-[11px] font-mono text-neutral-400 max-h-96 overflow-auto whitespace-pre-wrap leading-relaxed">
        {text}
      </pre>
    </div>
  )
}

// ---- Tab 1: Skills Library ----

type SkillItem = {
  _id: Id<"skills">
  name: string
  description: string
  content: string
  surface: string
  enabled: boolean
  default_on: boolean
  tools?: string[]
}

class SkillMetricsBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  render() { return this.state.hasError ? null : this.props.children }
}

type SkillActivationStat = {
  name: string
  count: number
  lastSeen: number
  projectCount: number
  userCount: number
  daily: Array<{ date: string; count: number }>
}

function SkillMetricsInner({ stats, onDrill }: { stats: SkillActivationStat[]; onDrill: (name: string) => void }) {
  if (!stats.length) return null

  const totalActivations = stats.reduce((sum, s) => sum + s.count, 0)
  const uniqueProjects = stats.reduce((sum, s) => sum + s.projectCount, 0)

  return (
    <div className="px-6 py-3 border-b border-neutral-800 flex-shrink-0">
      <div className="flex items-center gap-6">
        <div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Activations (30d)</div>
          <div className="text-lg font-semibold text-neutral-100 tabular-nums">{totalActivations.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Skills Used</div>
          <div className="text-lg font-semibold text-neutral-100">{stats.length}</div>
        </div>
        <div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Videos</div>
          <div className="text-lg font-semibold text-neutral-100">{uniqueProjects}</div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {stats.slice(0, 6).map((s) => (
            <button
              key={s.name}
              onClick={() => onDrill(s.name)}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-neutral-900 border border-neutral-800 hover:border-neutral-700 transition-colors group"
            >
              <span className="text-[11px] font-medium text-neutral-300 group-hover:text-white">{s.name}</span>
              <span className="text-[10px] text-neutral-500 tabular-nums">{s.count}</span>
              <Sparkline data={s.daily.map((d) => d.count)} color="#22d3ee" height={16} width={40} />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}


type SkillState = "disabled" | "enabled" | "default_on"
function getSkillState(s: SkillItem): SkillState {
  if (!s.enabled) return "disabled"
  if (s.default_on) return "default_on"
  return "enabled"
}

function SkillsTab() {
  const skills = useQuery(api.skills.listSkills, {})
  const setSkillState = useMutation(api.skills.setSkillState)
  const activationStats = useQuery(api.analytics.getEventStats, { event: "skill_activated", groupBy: "skillName", days: 30 })
  const [searchQuery, setSearchQuery] = useState('')
  const [drillSkill, setDrillSkill] = useState<string | null>(null)
  const [showMetrics, setShowMetrics] = useState(true)
  const [sortByActivations, setSortByActivations] = useState(false)

  const statsMap = useMemo(() => {
    const map: Record<string, SkillActivationStat> = {}
    if (activationStats) {
      for (const s of activationStats) map[s.name] = s
    }
    return map
  }, [activationStats])

  const filtered = useMemo(() => {
    if (!skills) return []
    return searchQuery
      ? skills.filter((s) =>
          s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : [...skills]
  }, [skills, searchQuery])

  const groups = useMemo(() => {
    if (!filtered.length) return { default_on: [], enabled: [], disabled: [] }
    return {
      default_on: filtered.filter((s) => getSkillState(s) === "default_on"),
      enabled: filtered.filter((s) => getSkillState(s) === "enabled"),
      disabled: filtered.filter((s) => getSkillState(s) === "disabled"),
    }
  }, [filtered])

  const sortedFlat = useMemo(() => {
    if (!sortByActivations) return null
    return [...filtered].sort((a, b) => (statsMap[b.name]?.count ?? 0) - (statsMap[a.name]?.count ?? 0))
  }, [sortByActivations, filtered, statsMap])

  if (drillSkill) {
    return <SkillRunsDrill skillName={drillSkill} onBack={() => setDrillSkill(null)} />
  }

  if (!skills) {
    return <div className="flex items-center justify-center h-32 text-neutral-600 text-sm">Loading skills...</div>
  }

  const defaultOnCount = skills.filter((s) => getSkillState(s) === "default_on").length
  const enabledCount = skills.filter((s) => getSkillState(s) === "enabled").length

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="px-6 py-3 border-b border-neutral-800 flex items-center gap-3 flex-shrink-0">
        <Search className="w-4 h-4 text-neutral-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Filter skills..."
          className="flex-1 bg-transparent text-sm text-neutral-200 placeholder-neutral-600 outline-none"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="text-neutral-500 hover:text-neutral-300">
            <X className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => setSortByActivations(!sortByActivations)}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded text-[11px] transition-colors',
            sortByActivations ? 'text-amber-400 bg-amber-500/10' : 'text-neutral-500 hover:text-neutral-300',
          )}
          title={sortByActivations ? 'Group by state' : 'Sort by activations'}
        >
          <ChevronsUpDown className="w-3 h-3" />
          {sortByActivations ? 'By activations' : 'By state'}
        </button>
        <button
          onClick={() => setShowMetrics(!showMetrics)}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded text-[11px] transition-colors',
            showMetrics ? 'text-cyan-400 bg-cyan-500/10' : 'text-neutral-500 hover:text-neutral-300',
          )}
          title={showMetrics ? 'Hide metrics' : 'Show metrics'}
        >
          <BarChart3 className="w-3 h-3" />
          Metrics
        </button>
        <div className="flex items-center gap-2 text-[11px] text-neutral-500 flex-shrink-0">
          {defaultOnCount > 0 && <span className="text-emerald-400 font-medium">{defaultOnCount} default on</span>}
          {defaultOnCount > 0 && enabledCount > 0 && <span>&middot;</span>}
          {enabledCount > 0 && <span className="text-blue-400 font-medium">{enabledCount} enabled</span>}
          <span>&middot;</span>
          <span>{skills.length} total</span>
        </div>
      </div>

      {showMetrics && activationStats && (
        <SkillMetricsBoundary>
          <SkillMetricsInner stats={activationStats} onDrill={setDrillSkill} />
        </SkillMetricsBoundary>
      )}

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {sortedFlat ? (
          <div className="grid gap-2">
            {sortedFlat.map((skill) => (
              <SkillCard key={skill._id} skill={skill} onSetState={setSkillState} stats={statsMap[skill.name]} onDrill={setDrillSkill} />
            ))}
          </div>
        ) : (
          <>
            {groups.default_on.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-[10px] font-medium text-emerald-400 uppercase tracking-wider flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Default On ({groups.default_on.length})
                  <span className="text-neutral-600 normal-case tracking-normal font-normal">— body in prompt, tools unlocked</span>
                </h3>
                <div className="grid gap-2">
                  {groups.default_on.map((skill) => (
                    <SkillCard key={skill._id} skill={skill} onSetState={setSkillState} stats={statsMap[skill.name]} onDrill={setDrillSkill} />
                  ))}
                </div>
              </div>
            )}
            {groups.enabled.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-[10px] font-medium text-blue-400 uppercase tracking-wider flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  Enabled ({groups.enabled.length})
                  <span className="text-neutral-600 normal-case tracking-normal font-normal">— description in prompt, agent activates on demand</span>
                </h3>
                <div className="grid gap-2">
                  {groups.enabled.map((skill) => (
                    <SkillCard key={skill._id} skill={skill} onSetState={setSkillState} stats={statsMap[skill.name]} onDrill={setDrillSkill} />
                  ))}
                </div>
              </div>
            )}
            {groups.disabled.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-[10px] font-medium text-neutral-600 uppercase tracking-wider flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
                  Disabled ({groups.disabled.length})
                  <span className="text-neutral-700 normal-case tracking-normal font-normal">— hidden from agent</span>
                </h3>
                <div className="grid gap-2">
                  {groups.disabled.map((skill) => (
                    <SkillCard key={skill._id} skill={skill} onSetState={setSkillState} stats={statsMap[skill.name]} onDrill={setDrillSkill} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const SKILL_STATES: SkillState[] = ["disabled", "enabled", "default_on"]
const SKILL_STATE_CONFIG: Record<SkillState, { label: string; color: string; bg: string; border: string }> = {
  disabled: { label: "Disabled", color: "text-neutral-500", bg: "bg-neutral-800", border: "border-neutral-700" },
  enabled: { label: "Enabled", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  default_on: { label: "Default On", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
}

function SkillStateSelector({ state, onChange, disabled }: {
  state: SkillState
  onChange: (s: SkillState) => void
  disabled?: boolean
}) {
  return (
    <div className={cn("flex rounded-md overflow-hidden border border-neutral-700", disabled && "opacity-50 pointer-events-none")}>
      {SKILL_STATES.map((s) => {
        const cfg = SKILL_STATE_CONFIG[s]
        const active = state === s
        return (
          <button
            key={s}
            onClick={() => onChange(s)}
            className={cn(
              "px-2 py-1 text-[10px] font-medium transition-colors",
              active ? `${cfg.bg} ${cfg.color} ${cfg.border}` : "text-neutral-600 hover:text-neutral-400 hover:bg-neutral-800/50",
            )}
          >
            {cfg.label}
          </button>
        )
      })}
    </div>
  )
}

function SkillCard({ skill, onSetState, stats, onDrill }: {
  skill: SkillItem
  onSetState: (args: { skillId: Id<"skills">; state: "disabled" | "enabled" | "default_on" }) => Promise<any>
  stats?: SkillActivationStat
  onDrill: (name: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [updating, setUpdating] = useState(false)
  const state = getSkillState(skill)

  const handleStateChange = async (newState: SkillState) => {
    if (newState === state) return
    setUpdating(true)
    try {
      await onSetState({ skillId: skill._id, state: newState })
    } finally {
      setUpdating(false)
    }
  }

  const toolCount = skill.tools?.length || 0
  const wordCount = skill.content.split(/\s+/).length

  return (
    <div className={cn(
      'border rounded-lg transition-colors',
      state === 'default_on' ? 'border-neutral-700 bg-neutral-900/30'
        : state === 'enabled' ? 'border-neutral-800 bg-neutral-900/20'
        : 'border-neutral-800/50 bg-neutral-950/50 opacity-60',
    )}>
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-sm font-medium text-neutral-100">{skill.name}</span>
            <Badge label={skill.surface} colorClass={SURFACE_COLORS[skill.surface] || SURFACE_COLORS.all} />
            {toolCount > 0 && (
              <span className="text-[10px] text-neutral-500">
                {toolCount} tool{toolCount !== 1 ? 's' : ''}
              </span>
            )}
            <span className="text-[10px] text-neutral-600">{wordCount}w</span>
          </div>
          <p className="text-xs text-neutral-400 leading-relaxed">{skill.description}</p>
          {skill.tools && skill.tools.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <Wrench className="w-3 h-3 text-neutral-600 flex-shrink-0" />
              {skill.tools.map((t) => (
                <span key={t} className="text-[10px] bg-neutral-800 text-neutral-400 px-1.5 py-0.5 rounded font-mono">
                  {t}
                </span>
              ))}
            </div>
          )}
          <button
            onClick={() => onDrill(skill.name)}
            className="flex items-center gap-3 mt-2 px-2 py-1 rounded bg-neutral-800/50 hover:bg-neutral-800 transition-colors group"
          >
            <span className={cn("text-[10px] tabular-nums", stats?.count ? "text-neutral-400" : "text-neutral-600")}>
              {stats?.count ?? 0} activation{(stats?.count ?? 0) !== 1 ? 's' : ''}
            </span>
            <span className={cn("text-[10px] tabular-nums", stats?.projectCount ? "text-neutral-500" : "text-neutral-600")}>
              {stats?.projectCount ?? 0} project{(stats?.projectCount ?? 0) !== 1 ? 's' : ''}
            </span>
            {stats && stats.lastSeen > 0 && (
              <span className="text-[10px] text-neutral-600">
                last {timeAgo(stats.lastSeen)}
              </span>
            )}
            {stats && stats.daily.length > 1 && (
              <Sparkline data={stats.daily.map((d) => d.count)} color="#22d3ee" height={14} width={48} />
            )}
            <ExternalLink className="w-2.5 h-2.5 text-neutral-700 group-hover:text-neutral-400 transition-colors" />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setExpanded(!expanded)}
            className={cn(
              'p-1.5 rounded transition-colors',
              expanded ? 'bg-neutral-800 text-neutral-300' : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800',
            )}
            title="View prompt"
          >
            <FileText className="w-4 h-4" />
          </button>
          <SkillStateSelector state={state} onChange={handleStateChange} disabled={updating} />
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-neutral-800/50">
          <div className="flex items-center gap-2 mt-3 mb-2">
            <span className="text-[10px] text-neutral-500">{skill.content.length} chars</span>
            <CopyBtn text={skill.content} />
          </div>
          <pre className="bg-neutral-950 border border-neutral-800 rounded-lg p-4 text-[11px] font-mono text-neutral-400 max-h-64 overflow-auto whitespace-pre-wrap leading-relaxed">
            {skill.content}
          </pre>
        </div>
      )}
    </div>
  )
}

function SkillRunsDrill({ skillName, onBack }: { skillName: string; onBack: () => void }) {
  const projects = useQuery(api.analytics.getEventProjects, {
    event: "skill_activated",
    filterKey: "skillName",
    filterValue: skillName,
    limit: 30,
  })

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="px-6 py-3 border-b border-neutral-800 flex items-center gap-3 flex-shrink-0">
        <button onClick={onBack} className="text-neutral-500 hover:text-neutral-300 transition-colors">
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-neutral-200">Videos using</span>
            <span className="text-sm font-semibold text-cyan-400">{skillName}</span>
          </div>
          <div className="text-[10px] text-neutral-500 mt-0.5">All activations</div>
        </div>
        <a
          href="/admin/observability"
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] font-medium text-neutral-400 hover:text-neutral-200 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 transition-colors"
        >
          <BarChart3 className="w-3 h-3" />
          Full Observability
        </a>
      </div>
      <div className="flex-1 overflow-auto">
        {!projects ? (
          <div className="flex items-center justify-center h-32 text-neutral-600 text-sm">Loading...</div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-2">
            <BarChart3 className="w-8 h-8 text-neutral-800" />
            <p className="text-sm text-neutral-500">No activations tracked yet for <span className="text-cyan-400 font-medium">{skillName}</span></p>
            <p className="text-[11px] text-neutral-600">Skill activation events are recorded automatically from now on.</p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-800/50">
            {projects.map((p) => (
              <a
                key={p.workflowId}
                href={`/video/${p.workflowId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 px-6 py-3 hover:bg-neutral-900/50 transition-colors group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-neutral-200 group-hover:text-white truncate">
                      {p.workflowTitle || 'Untitled'}
                    </span>
                    <span className="text-[10px] text-neutral-600 font-mono">{p.workflowId.slice(-8)}</span>
                  </div>
                  {p.userEmail && (
                    <span className="text-[10px] text-neutral-500 mt-0.5 block">{p.userEmail}</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-[10px] text-neutral-500 flex-shrink-0 tabular-nums">
                  <span>{p.count} activation{p.count !== 1 ? 's' : ''}</span>
                  <span>{timeAgo(p.timestamp)}</span>
                </div>
                <ExternalLink className="w-3 h-3 text-neutral-700 group-hover:text-neutral-400 transition-colors flex-shrink-0" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ---- Tab 2: Agent Conditionals ----

type VideoProvider = 'kling' | 'veo'
type SurfaceMode = 'web' | 'local' | 'nux'

interface SimState {
  videoProvider: VideoProvider
  verbosePrompts: boolean
  superMinimal: boolean
  unifiedPrompts: boolean
  surface: SurfaceMode
  isDesktop: boolean
  hasLibrary: boolean
}

const DEFAULT_SIM: SimState = {
  videoProvider: 'veo',
  verbosePrompts: false,
  superMinimal: false,
  unifiedPrompts: false,
  surface: 'web',
  isDesktop: false,
  hasLibrary: false,
}

function ConditionalsTab() {
  const [sim, setSim] = useState<SimState>(DEFAULT_SIM)

  const flagVideoProvider = useQuery(api.featureFlags.check, { flagKey: 'video_provider' })
  const flagVerbose = useQuery(api.featureFlags.check, { flagKey: 'verbose_prompts' })
  const flagSuperMinimal = useQuery(api.featureFlags.check, { flagKey: 'super_minimal' })
  const flagUnified = useQuery(api.featureFlags.check, { flagKey: 'unified_prompts' })

  const skills = useQuery(api.skills.listSkills, {})

  const activeSkills = useMemo(() => {
    if (!skills) return []
    return skills.filter((s) => s.default_on)
  }, [skills])

  const syncToProduction = useCallback(() => {
    setSim({
      videoProvider: (flagVideoProvider as VideoProvider) || 'veo',
      verbosePrompts: !!flagVerbose,
      superMinimal: !!flagSuperMinimal,
      unifiedPrompts: !!flagUnified,
      surface: 'web',
      isDesktop: false,
      hasLibrary: false,
    })
  }, [flagVideoProvider, flagVerbose, flagSuperMinimal, flagUnified])

  const toolSummary = useMemo(() => {
    const base = [
      'web_search', 'set_title', 'read_url_content', 'update_narrative',
      'update_settings', 'update_characters', 'create_clip', 'update_clip',
      'edit_timeline', 'delete_clip', 'generate_clip_video', 'generate_keyframe',
      'replace_keyframe', 'replace_clip_audio', 'apply_clip_motion_preset',
      'analyze_video_clip', 'add_video_overlay', 'apply_clip_filter',
    ]
    const optional: string[] = []
    if (sim.hasLibrary) optional.push('search_library', 'organize_library', 'label_assets')
    if (sim.isDesktop) optional.push('read_file', 'write_file', 'list_directory', 'run_command',
      'show_media_grid', 'show_clusters', 'show_folder_preview')
    const skillTools = activeSkills.flatMap((s) => s.tools || [])
    return { base, optional, skillTools, total: base.length + optional.length + skillTools.length }
  }, [sim.hasLibrary, sim.isDesktop, activeSkills])

  const skillsPromptText = useMemo(() => {
    if (activeSkills.length === 0) return ''
    return activeSkills.map((s) => `<skill name="${s.name}">\n${s.content}\n</skill>`).join('\n\n')
  }, [activeSkills])

  return (
    <div className="p-6 space-y-6 overflow-auto h-full">
      <div className="space-y-3">
        <h3 className="text-xs font-medium text-neutral-400 uppercase tracking-wider flex items-center gap-2">
          <Zap className="w-3.5 h-3.5" />
          Current Production Flags
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <FlagDisplay label="video_provider" value={flagVideoProvider} />
          <FlagDisplay label="verbose_prompts" value={flagVerbose} />
          <FlagDisplay label="super_minimal" value={flagSuperMinimal} />
          <FlagDisplay label="unified_prompts" value={flagUnified} />
        </div>
      </div>

      <div className="border-t border-neutral-800" />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-medium text-neutral-400 uppercase tracking-wider flex items-center gap-2">
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Simulation Controls
          </h3>
          <button
            onClick={syncToProduction}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-neutral-400 hover:text-neutral-200 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            Sync to production
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-[11px] text-neutral-500 font-medium">video_provider</label>
            <div className="flex gap-1">
              {(['kling', 'veo'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setSim({ ...sim, videoProvider: p })}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                    sim.videoProvider === p
                      ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                      : 'bg-neutral-900 text-neutral-500 border border-neutral-800 hover:text-neutral-300',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] text-neutral-500 font-medium">surface</label>
            <div className="flex gap-1">
              {(['web', 'local', 'nux'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSim({
                    ...sim,
                    surface: s,
                    isDesktop: s === 'local' || s === 'nux' ? true : sim.isDesktop,
                  })}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                    sim.surface === s
                      ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30'
                      : 'bg-neutral-900 text-neutral-500 border border-neutral-800 hover:text-neutral-300',
                  )}
                >
                  {s === 'web' && <Globe className="w-3 h-3" />}
                  {s === 'local' && <Monitor className="w-3 h-3" />}
                  {s === 'nux' && <Sparkles className="w-3 h-3" />}
                  {s}
                </button>
              ))}
            </div>
          </div>

          <SimToggle label="verbose_prompts" value={sim.verbosePrompts} onChange={(v) => setSim({ ...sim, verbosePrompts: v })} />
          <SimToggle label="super_minimal" value={sim.superMinimal} onChange={(v) => setSim({ ...sim, superMinimal: v })} />
          <SimToggle label="unified_prompts" value={sim.unifiedPrompts} onChange={(v) => setSim({ ...sim, unifiedPrompts: v })} />
          <SimToggle label="isDesktop" value={sim.isDesktop} onChange={(v) => setSim({ ...sim, isDesktop: v })} />
          <SimToggle label="hasLibrary" value={sim.hasLibrary} onChange={(v) => setSim({ ...sim, hasLibrary: v })} />
        </div>
      </div>

      <div className="border-t border-neutral-800" />

      <CollapsibleSection
        title="Available Tools"
        defaultOpen
        count={toolSummary.total}
        headerExtra={
          <span className="text-[10px] text-neutral-600">
            {toolSummary.base.length} base + {toolSummary.optional.length} optional + {toolSummary.skillTools.length} skill
          </span>
        }
      >
        <div className="space-y-3 mt-3">
          <div>
            <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1.5">Base Tools</div>
            <div className="flex flex-wrap gap-1">
              {toolSummary.base.map((t) => (
                <span key={t} className="text-[10px] bg-neutral-800 text-neutral-300 px-1.5 py-0.5 rounded font-mono">{t}</span>
              ))}
            </div>
          </div>
          {toolSummary.optional.length > 0 && (
            <div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1.5">Conditional Tools</div>
              <div className="flex flex-wrap gap-1">
                {toolSummary.optional.map((t) => (
                  <span key={t} className="text-[10px] bg-blue-500/10 text-blue-300 px-1.5 py-0.5 rounded font-mono border border-blue-500/20">{t}</span>
                ))}
              </div>
            </div>
          )}
          {toolSummary.skillTools.length > 0 && (
            <div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1.5">Skill-Gated Tools</div>
              <div className="flex flex-wrap gap-1">
                {toolSummary.skillTools.map((t) => (
                  <span key={t} className="text-[10px] bg-purple-500/10 text-purple-300 px-1.5 py-0.5 rounded font-mono border border-purple-500/20">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {skillsPromptText && (
        <CollapsibleSection title="Active Skills Prompt" count={activeSkills.length} copyText={skillsPromptText}>
          <PromptPreview text={skillsPromptText} />
        </CollapsibleSection>
      )}
    </div>
  )
}

function FlagDisplay({ label, value }: { label: string; value: boolean | string | null | undefined }) {
  const display = value === undefined ? 'loading...' : value === null ? 'not set' : String(value)
  const isOn = value === true || (typeof value === 'string' && value !== 'off')
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2.5">
      <div className="text-[10px] text-neutral-500 font-mono mb-1">{label}</div>
      <div className={cn(
        'text-sm font-medium',
        value === undefined ? 'text-neutral-600' : value === null ? 'text-neutral-600' : isOn ? 'text-emerald-400' : 'text-neutral-400',
      )}>
        {display}
      </div>
    </div>
  )
}

function SimToggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2.5">
      <span className="text-[11px] text-neutral-400 font-mono">{label}</span>
      <button onClick={() => onChange(!value)} className="transition-colors">
        {value
          ? <ToggleRight className="w-5 h-5 text-emerald-400" />
          : <ToggleLeft className="w-5 h-5 text-neutral-600" />
        }
      </button>
    </div>
  )
}

// ---- Tab 3: Playground ----

function PlaygroundTab() {
  const [search, setSearch] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState<Id<"workflows"> | null>(null)

  return (
    <div className="h-full flex flex-col">
      {!selectedProjectId ? (
        <ProjectBrowser search={search} setSearch={setSearch} onSelect={setSelectedProjectId} />
      ) : (
        <ProjectInspector projectId={selectedProjectId} onBack={() => setSelectedProjectId(null)} />
      )}
    </div>
  )
}

function ProjectBrowser({ search, setSearch, onSelect }: {
  search: string
  setSearch: (s: string) => void
  onSelect: (id: Id<"workflows">) => void
}) {
  const projects = useQuery(api.sandbox.browseProjects, { search: search || undefined, limit: 20 })

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-3 border-b border-neutral-800 flex items-center gap-3 flex-shrink-0">
        <Search className="w-4 h-4 text-neutral-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search videos by title..."
          className="flex-1 bg-transparent text-sm text-neutral-200 placeholder-neutral-600 outline-none"
        />
        {search && (
          <button onClick={() => setSearch('')} className="text-neutral-500 hover:text-neutral-300">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-auto">
        {!projects ? (
          <div className="flex items-center justify-center h-32 text-neutral-600 text-sm">Loading videos...</div>
        ) : projects.projects.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-neutral-600 text-sm">No videos found</div>
        ) : (
          <div className="divide-y divide-neutral-800/50">
            {projects.projects.map((p) => (
              <button
                key={p._id}
                onClick={() => onSelect(p._id)}
                className="w-full text-left px-6 py-3 hover:bg-neutral-900/50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-neutral-200 group-hover:text-white truncate">{p.title}</span>
                      {p.mode && (
                        <span className="text-[10px] bg-neutral-800 text-neutral-500 px-1.5 py-0.5 rounded">{p.mode}</span>
                      )}
                    </div>
                    {p.narrativeSnippet && (
                      <p className="text-xs text-neutral-500 mt-0.5 truncate">{p.narrativeSnippet}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-neutral-600">
                      {new Date(p._creationTime).toLocaleDateString()}
                    </span>
                    <ArrowRight className="w-3 h-3 text-neutral-700 group-hover:text-neutral-400 transition-colors" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ProjectInspector({ projectId, onBack }: {
  projectId: Id<"workflows">
  onBack: () => void
}) {
  const [obs, setObs] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [messageSlice, setMessageSlice] = useState<number>(0)
  const [allExpanded, setAllExpanded] = useState(false)
  const getObservability = useAction(api.workflowAgent.getAgentObservability)
  const project = useQuery(api.workflows.get, { id: projectId })

  const loadObs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getObservability({ workflowId: projectId })
      setObs(data)
      setMessageSlice(data.stats.sourceMessageCount)
    } catch (e: any) {
      setError(e.message || 'Failed to load observability data')
    } finally {
      setLoading(false)
    }
  }, [getObservability, projectId])

  if (!obs && !loading && !error) {
    loadObs()
  }

  const slicedSourceMessages = useMemo(() => {
    if (!obs) return []
    return obs.sourceMessages.slice(0, messageSlice)
  }, [obs, messageSlice])

  const projectTitle = project?.title || 'Untitled'

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-neutral-500 text-sm">
        <div className="flex items-center gap-3">
          <div className="w-4 h-4 border-2 border-neutral-600 border-t-neutral-300 rounded-full animate-spin" />
          Loading agent state...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 space-y-3">
        <button onClick={onBack} className="text-xs text-neutral-500 hover:text-neutral-300 flex items-center gap-1">
          <ChevronRight className="w-3 h-3 rotate-180" /> Back to projects
        </button>
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-sm text-red-400">{error}</div>
      </div>
    )
  }

  if (!obs) return null

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="px-6 py-3 border-b border-neutral-800 flex items-center gap-3 flex-shrink-0">
        <button onClick={onBack} className="text-neutral-500 hover:text-neutral-300 transition-colors">
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-neutral-200">{projectTitle}</span>
            <span className="text-[10px] text-neutral-600 font-mono">{projectId.slice(-8)}</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-neutral-500 mt-0.5">
            <span>Model: <span className="text-neutral-400">{obs.model}</span></span>
            <span>{obs.stats.sourceMessageCount} msgs</span>
            <span>{obs.stats.clipCount} clips</span>
            <span>{obs.stats.replayMessageCount} replay</span>
          </div>
        </div>
        <button
          onClick={() => setAllExpanded(!allExpanded)}
          className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-300 px-2 py-1 rounded hover:bg-neutral-800 transition-colors"
          title={allExpanded ? 'Collapse all' : 'Expand all'}
        >
          <ChevronsUpDown className="w-3.5 h-3.5" />
          {allExpanded ? 'Collapse' : 'Expand'}
        </button>
        <button
          onClick={loadObs}
          className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-300 px-2 py-1 rounded hover:bg-neutral-800 transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {obs.stats.sourceMessageCount > 0 && (
        <div className="px-6 py-3 border-b border-neutral-800 flex items-center gap-4 flex-shrink-0">
          <Play className="w-3.5 h-3.5 text-neutral-500 flex-shrink-0" />
          <div className="flex-1 flex items-center gap-3">
            <span className="text-[11px] text-neutral-500 whitespace-nowrap flex-shrink-0 tabular-nums">
              Messages 1&ndash;{messageSlice} of {obs.stats.sourceMessageCount}
            </span>
            <input
              type="range"
              min={1}
              max={obs.stats.sourceMessageCount}
              value={messageSlice}
              onChange={(e) => setMessageSlice(parseInt(e.target.value))}
              className="flex-1 h-1 bg-neutral-800 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-400 [&::-webkit-slider-thumb]:shadow-[0_0_6px_rgba(34,211,238,0.4)]"
            />
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto p-6 space-y-4">
        <CollapsibleSection
          title="System Prompt"
          defaultOpen={allExpanded}
          copyText={obs.systemPrompt}
          headerExtra={
            <span className="text-[10px] text-neutral-600">{obs.systemPrompt.length.toLocaleString()} chars / ~{Math.round(obs.systemPrompt.length / 4).toLocaleString()} tokens</span>
          }
        >
          <pre className="mt-3 bg-neutral-950 border border-neutral-800 rounded-lg p-4 text-[11px] font-mono text-neutral-400 max-h-96 overflow-auto whitespace-pre-wrap leading-relaxed">
            {obs.systemPrompt}
          </pre>
        </CollapsibleSection>

        <CollapsibleSection title="Tools" count={obs.tools.length} defaultOpen={allExpanded}>
          <div className="mt-3 space-y-2">
            {obs.tools.map((tool: any, i: number) => (
              <ToolEntry key={i} tool={tool} />
            ))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Context Message"
          defaultOpen={allExpanded}
          copyText={obs.contextMessage}
          headerExtra={
            <span className="text-[10px] text-neutral-600">{obs.contextMessage.length.toLocaleString()} chars</span>
          }
        >
          <pre className="mt-3 bg-neutral-950 border border-neutral-800 rounded-lg p-4 text-[11px] font-mono text-neutral-400 max-h-64 overflow-auto whitespace-pre-wrap leading-relaxed">
            {obs.contextMessage}
          </pre>
        </CollapsibleSection>

        <CollapsibleSection title="Replay Messages" count={obs.replayMessages.length} defaultOpen={allExpanded}>
          <div className="mt-3 space-y-2">
            {obs.replayMessages.map((msg: any, i: number) => (
              <div key={i} className="border border-neutral-800 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded font-medium uppercase',
                    msg.role === 'user' ? 'bg-blue-500/15 text-blue-400' :
                    msg.role === 'assistant' ? 'bg-emerald-500/15 text-emerald-400' :
                    'bg-neutral-500/15 text-neutral-400',
                  )}>
                    {msg.role}
                  </span>
                  <span className="text-[10px] text-neutral-600">
                    {Array.isArray(msg.content) ? `${msg.content.length} blocks` : `${String(msg.content).length} chars`}
                  </span>
                </div>
                <pre className="text-[11px] font-mono text-neutral-500 max-h-32 overflow-auto whitespace-pre-wrap">
                  {Array.isArray(msg.content)
                    ? msg.content.map((b: any) => {
                        if (b.type === 'text') return b.text
                        if (b.type === 'tool_use') return `[tool_use: ${b.name}(${JSON.stringify(b.input).slice(0, 120)}...)]`
                        if (b.type === 'tool_result') return `[tool_result: ${b.tool_use_id} -> ${String(b.content).slice(0, 120)}...]`
                        return JSON.stringify(b).slice(0, 200)
                      }).join('\n')
                    : String(msg.content).slice(0, 2000)
                  }
                </pre>
              </div>
            ))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Source Conversation" count={slicedSourceMessages.length} defaultOpen={allExpanded}>
          <div className="mt-3 space-y-2">
            {slicedSourceMessages.map((msg: any, i: number) => (
              <div key={msg.id || i} className={cn(
                'border rounded-lg p-3',
                msg.role === 'user' ? 'border-blue-500/20 bg-blue-500/5' :
                msg.role === 'assistant' ? 'border-emerald-500/20 bg-emerald-500/5' :
                'border-neutral-800',
              )}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded font-medium uppercase',
                    msg.role === 'user' ? 'bg-blue-500/15 text-blue-400' :
                    msg.role === 'assistant' ? 'bg-emerald-500/15 text-emerald-400' :
                    'bg-neutral-500/15 text-neutral-400',
                  )}>
                    {msg.role}
                  </span>
                  <span className="text-[10px] text-neutral-600">
                    {new Date(msg.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {msg.isStreaming && <span className="text-[10px] text-amber-400">streaming</span>}
                  {msg.aborted && <span className="text-[10px] text-red-400">aborted</span>}
                  {msg.toolCalls?.length > 0 && (
                    <span className="text-[10px] text-purple-400 flex items-center gap-1">
                      <Wrench className="w-2.5 h-2.5" />
                      {msg.toolCalls.length} tool calls
                    </span>
                  )}
                </div>
                <pre className="text-[11px] font-mono text-neutral-400 max-h-40 overflow-auto whitespace-pre-wrap">
                  {msg.content ? msg.content.slice(0, 2000) : '(empty)'}
                  {msg.content && msg.content.length > 2000 ? '...' : ''}
                </pre>
                {msg.toolCalls?.length > 0 && (
                  <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-purple-500/20">
                    {msg.toolCalls.map((tc: any, j: number) => (
                      <div key={j} className="text-[10px]">
                        <span className="font-mono text-purple-300">{tc.name || tc.toolName}</span>
                        <span className="text-neutral-600 ml-1.5">
                          {JSON.stringify(tc.input || tc.args || {}).slice(0, 100)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {msg.toolResults?.length > 0 && (
                  <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-emerald-500/20">
                    {msg.toolResults.map((tr: any, j: number) => (
                      <div key={j} className="text-[10px]">
                        <span className="font-mono text-emerald-300">{tr.name || tr.toolName}</span>
                        <span className={cn('ml-1.5', tr.success === false ? 'text-red-400' : 'text-neutral-600')}>
                          {JSON.stringify(tr.result || tr.output || {}).slice(0, 100)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      </div>
    </div>
  )
}

function ToolEntry({ tool }: { tool: any }) {
  const [expanded, setExpanded] = useState(false)
  const name = tool.name || tool.type || 'unknown'
  const desc = tool.description || ''
  const schema = tool.input_schema

  return (
    <div className="border border-neutral-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-neutral-900/50 transition-colors"
      >
        {expanded ? <ChevronDown className="w-3 h-3 text-neutral-600" /> : <ChevronRight className="w-3 h-3 text-neutral-600" />}
        <span className="text-xs font-mono text-amber-300">{name}</span>
        {desc && <span className="text-[10px] text-neutral-600 truncate flex-1">{desc.slice(0, 80)}{desc.length > 80 ? '...' : ''}</span>}
        {schema?.properties && (
          <span className="text-[10px] text-neutral-700 flex-shrink-0">
            {Object.keys(schema.properties).length} params
          </span>
        )}
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-neutral-800/50 space-y-2">
          {desc && (
            <p className="text-[11px] text-neutral-400 mt-2 leading-relaxed">{desc}</p>
          )}
          {schema && (
            <pre className="bg-neutral-950 border border-neutral-800 rounded p-2 text-[10px] font-mono text-neutral-500 max-h-48 overflow-auto">
              {JSON.stringify(schema, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

// ---- Main Page ----

export default function AdminAgentPage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<TabId>('skills')
  const skills = useQuery(api.skills.listSkills, user?.isAdmin ? {} : 'skip')
  const activeCount = skills?.filter((s) => s.default_on).length

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      const tabs: TabId[] = ['skills', 'conditionals', 'playground']
      if (e.key >= '1' && e.key <= '3') {
        e.preventDefault()
        setActiveTab(tabs[parseInt(e.key) - 1])
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  if (!user?.isAdmin) {
    return (
      <div className="flex items-center justify-center h-full text-neutral-500">
        Admin access required
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-neutral-950 text-neutral-100 overflow-hidden">
      <div className="border-b border-neutral-800 px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
            <Bot className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-semibold tracking-tight">Agent Playground</h1>
            <p className="text-xs text-neutral-500">Skills, conditionals, and prompt inspection</p>
          </div>
          <a
            href="/admin/observability"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium text-neutral-400 hover:text-neutral-200 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 transition-colors"
          >
            <BarChart3 className="w-3 h-3" />
            LLM Observability
          </a>
        </div>

        <div className="flex gap-1 bg-neutral-900 rounded-lg p-1 w-fit">
          {([
            { id: 'skills' as const, label: 'Skills Library', icon: BookOpen, badge: activeCount, key: '1' },
            { id: 'conditionals' as const, label: 'Agent Conditionals', icon: SlidersHorizontal, key: '2' },
            { id: 'playground' as const, label: 'Playground', icon: FlaskConical, key: '3' },
          ]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-neutral-800 text-white shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-300',
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {'badge' in tab && tab.badge != null && tab.badge > 0 && (
                <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded-full border border-emerald-500/20">
                  {tab.badge}
                </span>
              )}
              <span className="text-[9px] text-neutral-600 ml-0.5">{tab.key}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {activeTab === 'skills' && <SkillsTab />}
        {activeTab === 'conditionals' && <ConditionalsTab />}
        {activeTab === 'playground' && <PlaygroundTab />}
      </div>
    </div>
  )
}
